import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useJsApiLoader } from '@react-google-maps/api';
import Delete from '../../../../img/icons/svg/botones/delete.png';
import Icon from 'components/blocks/Icon/Icon';
import { safeFetch } from '../../../../utils/security';
import { reducedcreateZipFile, reducedFileToBase64 } from './FormInputArchivoReducido';
import './sccs/CalculadoraGastosOtrosVehiculos.scss';

interface IGooglePlaceAutocompleteProps {
  id: string;
  initialValue?: string;
  isGoogleMapsLoaded: boolean;
  hasSelectedPlace: boolean;
  label: string;
  placeholder: string;
  onUsePersonalAddress?: () => void;
  personalAddressDisabled?: boolean;
  placeIdRef: React.RefObject<string | null>;
  valueRef: React.RefObject<IGooglePlaceAutocompleteElement | null>;
  onChange: (value: string) => void;
  onError: (message: string) => void;
}

interface IGooglePlaceAutocompleteElement extends HTMLElement {
  description?: string;
  disabled?: boolean;
  includedRegionCodes?: string[];
  noInputIcon?: boolean;
  placeholder?: string;
  requestedLanguage?: string;
  requestedRegion?: string;
  value: string;
}

interface IGooglePlacesNewLibrary {
  PlaceAutocompleteElement: new (options?: {
    includedRegionCodes?: string[];
    placeholder?: string;
    requestedLanguage?: string;
    requestedRegion?: string;
  }) => IGooglePlaceAutocompleteElement;
}

interface IPlacePredictionSelectEvent extends Event {
  placePrediction?: {
    placeId?: string;
    text?: { text?: string };
    toPlace: () => google.maps.places.Place;
  };
}

interface IOtherVehicleExpenseValue {
  Id?: string | null;
  Tipo: string | null;
  TipoDescripcion: string | null;
  FechaViaje: string | null;
  Importe: number | null;
  DireccionOrigen: string;
  DireccionDestino: string;
  OrigenGooglePlaceId: string | null;
  DestinoGooglePlaceId: string | null;
  Ticket: null;
  Archivos: File[];
  SasUri: string | null;
  FechaPresentacion: string | null;
  Nombre: string | null;
  NombreStorage: string | null;
  Extension: string | null;
}

interface IModalValidationErrors {
  tipo?: string;
  travelDate?: string;
  importe?: string;
  origin?: string;
  destination?: string;
  tickets?: string;
}

interface ICalculadoraGastosOtrosVehiculosProps {
  name: string;
  id?: string;
  label?: string;
  req?: string | boolean;
  required?: string | boolean;
  additionalRequiredNames?: string[];
  invisible?: boolean;
  xl?: number;
  md?: number;
  col?: number;
  disabled?: boolean;
  setTotalSize?: React.Dispatch<React.SetStateAction<number>>;
}

const OTHER_VEHICLE_TYPE_OPTIONS = [
  { value: 'TAXI', label: 'Taxi' },
  { value: 'TRANSPORTE_PUBLICO', label: 'Transporte público' },
  { value: 'UBER_VTC', label: 'Uber / VTC' },
  { value: 'OTROS', label: 'Otros' },
];

const ALLOWED_FILE_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.pdf', '.docx', '.doc',
  '.xlsx', '.xls', '.pptx', '.ppt', '.zip', '.7z', '.txt',
  '.csv', '.odt', '.ods', '.webp',
];

const getFileSizeMb = (files: File[]) => files.reduce(
  (total, file) => total + file.size / (1024 * 1024),
  0
);

const formatFileSize = (sizeInBytes: number) => {
  if (sizeInBytes >= 1024 * 1024) {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  return `${Math.max(sizeInBytes / 1024, 0.01).toFixed(2)} KB`;
};

const getUnknownErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallbackMessage;
};

const formatDateForDisplay = (value: unknown) => {
  const valueAsString = typeof value === 'string' ? value : '';
  const [year, month, day] = valueAsString.split('-');

  if (!year || !month || !day) {
    return '-';
  }

  return `${day}/${month}/${year}`;
};

const parseImporte = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;

  const normalizedValue = String(value).trim().replace(',', '.');
  if (!/^\d+(?:\.\d+)?$/.test(normalizedValue)) return null;

  const importe = Number(normalizedValue);
  return Number.isFinite(importe) ? importe : null;
};

const formatImporte = (value: number | null) => value === null
  ? '-'
  : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);

const getTypeLabel = (value: string | null | undefined) => (
  OTHER_VEHICLE_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? '-'
);

const formatDateForInput = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  return value.split('T')[0];
};

const getTodayInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatPresentationDate = (value: string | null | undefined) => {
  if (!value) return '-';

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? formatDateForDisplay(value)
    : new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
};

const getPersistedDownloadName = (expense: IOtherVehicleExpenseValue) => {
  const extension = expense.Extension?.trim().replace(/^\./, '') ?? '';
  const originalName = expense.Nombre?.trim();

  if (originalName) {
    return extension && !originalName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)
      ? `${originalName}.${extension}`
      : originalName;
  }

  return extension.toLowerCase() === 'zip'
    ? 'DocumentacionZipPortal.zip'
    : `ticket-presentado${extension ? `.${extension}` : ''}`;
};

const getTypeValueFromCode = (value: unknown) => {
  if (typeof value === 'string' && OTHER_VEHICLE_TYPE_OPTIONS.some((option) => option.value === value)) {
    return value;
  }

  const parsedValue = Number(value);
  return OTHER_VEHICLE_TYPE_OPTIONS[Number.isFinite(parsedValue) ? parsedValue - 1 : -1]?.value ?? null;
};

const getTypeCodeFromValue = (value: unknown) => {
  const optionIndex = OTHER_VEHICLE_TYPE_OPTIONS.findIndex((option) => option.value === value);
  return optionIndex >= 0 ? optionIndex + 1 : null;
};

const getValidationMessages = (errors: IModalValidationErrors) => (
  Object.values(errors).filter((message): message is string => typeof message === 'string' && message.trim() !== '')
);

export const adaptGastosOtrosVehiculos = (expenses: unknown): IOtherVehicleExpenseValue[] => {
  if (!Array.isArray(expenses)) return [];

  return expenses.map((expense) => {
    const expenseData = expense as Record<string, unknown>;
    const typeValue = getTypeValueFromCode(expenseData.TipoTransporte);

    return {
      Id: (expenseData.Id as string | null | undefined) ?? null,
      Tipo: typeValue,
      TipoDescripcion: typeValue ? getTypeLabel(typeValue) : null,
      FechaViaje: formatDateForInput(expenseData.FechaViaje),
      Importe: parseImporte(expenseData.Importe),
      DireccionOrigen: String(expenseData.OrigenViaje ?? ''),
      DireccionDestino: String(expenseData.DestinoViaje ?? ''),
      OrigenGooglePlaceId: (expenseData.OrigenGooglePlaceId as string | null | undefined) ?? null,
      DestinoGooglePlaceId: (expenseData.DestinoGooglePlaceId as string | null | undefined) ?? null,
      Ticket: null,
      Archivos: [],
      SasUri: typeof expenseData.SasUri === 'string' ? expenseData.SasUri : null,
      FechaPresentacion: typeof expenseData.FechaPresentacion === 'string' ? expenseData.FechaPresentacion : null,
      Nombre: typeof expenseData.Nombre === 'string' ? expenseData.Nombre : null,
      NombreStorage: typeof expenseData.NombreStorage === 'string' ? expenseData.NombreStorage : null,
      Extension: typeof expenseData.Extension === 'string' ? expenseData.Extension : null,
    };
  });
};

export const exportGastosOtrosVehiculos = async (expenses: unknown) => {
  if (!Array.isArray(expenses)) return [];

  return Promise.all(expenses.map(async (expense) => {
    const expenseData = expense as Partial<IOtherVehicleExpenseValue>;
    const files = Array.isArray(expenseData.Archivos) ? expenseData.Archivos : [];
    const hasMultipleFiles = files.length > 1;
    const file = files[0];
    const extensionIndex = file?.name.lastIndexOf('.') ?? -1;
    const extension = extensionIndex >= 0 ? file.name.slice(extensionIndex + 1) : '';

    return {
      Id: expenseData.Id ?? null,
      TipoTransporte: getTypeCodeFromValue(expenseData.Tipo),
      FechaViaje: expenseData.FechaViaje ?? null,
      Importe: parseImporte(expenseData.Importe),
      OrigenViaje: expenseData.DireccionOrigen ?? null,
      DestinoViaje: expenseData.DireccionDestino ?? null,
      Nombre: hasMultipleFiles ? 'DocumentacionZipPortal.zip' : file?.name ?? null,
      Extension: hasMultipleFiles ? 'zip' : extension || null,
      Base64: hasMultipleFiles
        ? await reducedcreateZipFile(files)
        : file
          ? await reducedFileToBase64(file)
          : null,
      FechaPresentacion: files.length > 0 ? new Date().toISOString() : null,
    };
  }));
};

const GooglePlaceAutocomplete: React.FC<IGooglePlaceAutocompleteProps> = ({
  id,
  initialValue = '',
  isGoogleMapsLoaded,
  hasSelectedPlace,
  label,
  placeholder,
  onUsePersonalAddress,
  personalAddressDisabled,
  placeIdRef,
  valueRef,
  onChange,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const searchRequestIdRef = useRef(0);
  const searchRevealTimeoutRef = useRef<number | null>(null);
  const [searchText, setSearchText] = useState(initialValue);
  const searchTextRef = useRef(searchText);
  const [typedSearchText, setTypedSearchText] = useState('');
  const [showSearchMore, setShowSearchMore] = useState(false);
  const [inputRevision, setInputRevision] = useState(0);
  const [searchResults, setSearchResults] = useState<google.maps.places.Place[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  onChangeRef.current = onChange;
  onErrorRef.current = onError;
  searchTextRef.current = searchText;

  const restartSearchReveal = useCallback(() => {
    if (searchRevealTimeoutRef.current !== null) {
      window.clearTimeout(searchRevealTimeoutRef.current);
      searchRevealTimeoutRef.current = null;
    }
    setShowSearchMore(false);
    setInputRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    searchRequestIdRef.current += 1;
    if (valueRef.current && valueRef.current.value !== initialValue) {
      valueRef.current.value = initialValue;
    }
    setSearchText(initialValue);
    setSearchResults([]);
    setSearchError(null);
    setIsSearching(false);
  }, [initialValue, valueRef]);

  useEffect(() => {
    setShowSearchMore(false);
    if (!isGoogleMapsLoaded || hasSelectedPlace || typedSearchText.trim().length < 3 || typedSearchText !== searchText) {
      return;
    }

    const timeout = window.setTimeout(() => {
      searchRevealTimeoutRef.current = null;
      setShowSearchMore(true);
    }, 1000);
    searchRevealTimeoutRef.current = timeout;
    return () => {
      window.clearTimeout(timeout);
      if (searchRevealTimeoutRef.current === timeout) searchRevealTimeoutRef.current = null;
    };
  }, [hasSelectedPlace, inputRevision, isGoogleMapsLoaded, searchText, typedSearchText]);

  const searchMorePlaces = async () => {
    const query = (valueRef.current?.value ?? searchText).trim();
    if (!isGoogleMapsLoaded || query.length < 3 || isSearching) return;

    const requestId = ++searchRequestIdRef.current;
    setIsSearching(true);
    setSearchResults([]);
    setSearchError(null);

    try {
      const { Place } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
      const { places } = await Place.searchByText({
        textQuery: query,
        fields: ['id', 'displayName', 'formattedAddress', 'businessStatus'],
        language: 'es',
        region: 'es',
        maxResultCount: 10,
      });

      if (requestId !== searchRequestIdRef.current) return;
      const selectablePlaces = places.filter((place) => place.id && (place.formattedAddress || place.displayName));
      setSearchResults(selectablePlaces);
      if (selectablePlaces.length === 0) {
        setSearchError('Google Maps no ha encontrado lugares con ese texto. Prueba con la dirección completa.');
      }
    } catch (error: unknown) {
      if (requestId !== searchRequestIdRef.current) return;
      setSearchError(getUnknownErrorMessage(error, 'No se ha podido buscar el lugar en Google Maps.'));
    } finally {
      if (requestId === searchRequestIdRef.current) setIsSearching(false);
    }
  };

  const selectSearchResult = (place: google.maps.places.Place) => {
    const address = place.formattedAddress || place.displayName || '';
    if (!place.id || !address) return;

    searchRequestIdRef.current += 1;
    placeIdRef.current = place.id;
    if (valueRef.current) valueRef.current.value = address;
    setSearchText(address);
    setTypedSearchText('');
    setSearchResults([]);
    setSearchError(null);
    setIsSearching(false);
    onChangeRef.current(address);
  };

  useEffect(() => {
    if (!isGoogleMapsLoaded || !containerRef.current) {
      return;
    }

    let autocomplete: IGooglePlaceAutocompleteElement | null = null;
    let disposed = false;

    const initializeAutocomplete = async () => {
      try {
        const placesLibrary = (await (
          google.maps.importLibrary as unknown as (libraryName: string) => Promise<unknown>
        )('places')) as IGooglePlacesNewLibrary;

        if (disposed || !containerRef.current) {
          return;
        }

        autocomplete = new placesLibrary.PlaceAutocompleteElement({
          includedRegionCodes: ['es'],
          placeholder,
          requestedLanguage: 'es',
          requestedRegion: 'es',
        });

        autocomplete.id = id;
        autocomplete.className = 'calculadora-gastos-otros-vehiculos__places-new';
        autocomplete.description = label;
        autocomplete.noInputIcon = true;
        autocomplete.value = initialValue;
        Object.assign(autocomplete.style, {
          backgroundColor: 'transparent',
          border: '0',
          boxShadow: 'none',
          outline: '0',
        });
        valueRef.current = autocomplete;

        const handleInput = () => {
          searchRequestIdRef.current += 1;
          placeIdRef.current = null;
          const value = autocomplete?.value ?? '';
          restartSearchReveal();
          setSearchText(value);
          setTypedSearchText(value);
          setSearchResults([]);
          setSearchError(null);
          setIsSearching(false);
          onChangeRef.current(value);
        };

        const handleKeyDown = () => {
          restartSearchReveal();
        };

        const handleWidgetClick = () => {
          window.setTimeout(() => {
            if (autocomplete && !autocomplete.value.trim() && searchTextRef.current.trim()) handleInput();
          }, 0);
        };

        const handleSelect = async (event: Event) => {
          try {
            const selectEvent = event as IPlacePredictionSelectEvent;
            const placePrediction = selectEvent.placePrediction;

            if (!placePrediction) {
              return;
            }

            const selectedPlace = placePrediction.toPlace();
            placeIdRef.current = selectedPlace.id || placePrediction.placeId || null;

            await selectedPlace.fetchFields({ fields: ['id', 'formattedAddress', 'displayName'] });

            const selectedAddress = selectedPlace.formattedAddress || selectedPlace.displayName || placePrediction.text?.text || autocomplete?.value || '';
            placeIdRef.current = selectedPlace.id || placeIdRef.current;

            if (autocomplete) {
              autocomplete.value = selectedAddress;
            }

            searchRequestIdRef.current += 1;
            setSearchText(selectedAddress);
            setTypedSearchText('');
            setSearchResults([]);
            setSearchError(null);
            onChangeRef.current(selectedAddress);
          } catch (error: unknown) {
            placeIdRef.current = null;
            onErrorRef.current(
              getUnknownErrorMessage(
                error,
                'No se ha podido obtener el detalle de Places API (New). Revisa que Places API (New) esté habilitada.'
              )
            );
          }
        };

        const handlePlacesError = (event: Event) => {
          const errorEvent = event as Event & { error?: { message?: string }; message?: string };

          placeIdRef.current = null;
          onErrorRef.current(
            errorEvent.error?.message ||
              errorEvent.message ||
              'Places API (New) ha denegado la petición. Revisa que Places API (New) esté habilitada para esta API key.'
          );
        };

        autocomplete.addEventListener('input', handleInput);
        autocomplete.addEventListener('change', handleInput);
        autocomplete.addEventListener('keydown', handleKeyDown, true);
        autocomplete.addEventListener('click', handleWidgetClick, true);
        autocomplete.addEventListener('gmp-select', handleSelect);
        autocomplete.addEventListener('gmp-error', handlePlacesError);

        containerRef.current.replaceChildren(autocomplete);
      } catch (error: unknown) {
        onErrorRef.current(
          getUnknownErrorMessage(
            error,
            'No se ha podido inicializar Places API (New). Revisa que Maps JavaScript API y Places API (New) estén habilitadas.'
          )
        );
      }
    };

    void initializeAutocomplete();

    return () => {
      disposed = true;
      searchRequestIdRef.current += 1;
      autocomplete?.remove();
      autocomplete = null;
      valueRef.current = null;
    };
  }, [id, isGoogleMapsLoaded, label, placeIdRef, placeholder, restartSearchReveal, valueRef]);

  return (
    <div className="calculadora-gastos-otros-vehiculos__places-search">
      <div className="calculadora-gastos-otros-vehiculos__field-heading">
        <label htmlFor={id}>{label}</label>
        {(onUsePersonalAddress || (showSearchMore && !hasSelectedPlace)) && (
          <div className="calculadora-gastos-otros-vehiculos__field-actions">
            {showSearchMore && !hasSelectedPlace && (
              <button
                type="button"
                className="calculadora-gastos-otros-vehiculos__search-more-button"
                onClick={searchMorePlaces}
                disabled={!isGoogleMapsLoaded || isSearching}
              >
                <i className="fa fa-search" aria-hidden="true" />
                <span>{isSearching ? 'Buscando...' : 'Buscar más lugares'}</span>
              </button>
            )}
            {onUsePersonalAddress && (
              <button
                type="button"
                className="calculadora-gastos-otros-vehiculos__personal-address-button"
                onClick={onUsePersonalAddress}
                disabled={personalAddressDisabled}
              >
                <i className="fa fa-home" aria-hidden="true" />
                <span>Domicilio Personal</span>
              </button>
            )}
          </div>
        )}
      </div>
      <div ref={containerRef} className="calculadora-gastos-otros-vehiculos__places-container" />
      {(searchResults.length > 0 || searchError) && (
        <div className="calculadora-gastos-otros-vehiculos__search-results">
          {searchError ? (
            <p role="status">{searchError}</p>
          ) : (
            <ul aria-label={`Resultados adicionales de Google Maps para ${label}`}>
              {searchResults.map((place) => (
                <li key={place.id}>
                  <button type="button" onClick={() => selectSearchResult(place)}>
                    <strong>{place.displayName || place.formattedAddress}</strong>
                    {place.formattedAddress && <span>{place.formattedAddress}</span>}
                    {place.businessStatus === google.maps.places.BusinessStatus.CLOSED_PERMANENTLY && (
                      <span>Cerrado permanentemente</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <span className="calculadora-gastos-otros-vehiculos__search-attribution">Resultados de Google Maps</span>
        </div>
      )}
    </div>
  );
};

const CalculadoraGastosOtrosVehiculos: React.FC<ICalculadoraGastosOtrosVehiculosProps> = ({
  name,
  id,
  label = 'Viajes otros vehículos',
  req,
  required,
  additionalRequiredNames = [],
  invisible = false,
  xl = 12,
  md = 12,
  col = 12,
  disabled = false,
  setTotalSize,
}) => {
  const {
    control,
    formState: { errors },
    getValues,
    trigger,
  } = useFormContext();

  const requiredValue = req ?? required ?? false;
  const requiredMessage = requiredValue === true ? 'Debe añadir al menos un viaje' : requiredValue || undefined;
  const modalBaseId = `${id ?? name}-modal`;
  const hasRequiredRows = (value: unknown) => Array.isArray(value) && value.length > 0;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
    rules: requiredMessage
      ? {
        validate: (value) => (
          hasRequiredRows(value) || additionalRequiredNames.some((fieldName) => hasRequiredRows(getValues(fieldName)))
            ? true
            : requiredMessage
        ),
      }
      : undefined,
  });

  const expenseRows = fields as unknown as Array<IOtherVehicleExpenseValue & { id: string }>;
  const rawErrorMessage = get(errors, `${name}.root.message`) || get(errors, `${name}.message`);
  const errorMessage = typeof rawErrorMessage === 'string' ? rawErrorMessage : undefined;

  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tipo, setTipo] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [importe, setImporte] = useState('');
  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [originHasSelectedPlace, setOriginHasSelectedPlace] = useState(false);
  const [destinationHasSelectedPlace, setDestinationHasSelectedPlace] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewRowIndex, setPreviewRowIndex] = useState<number | null>(null);
  const [downloadRowIndex, setDownloadRowIndex] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [modalValidationErrors, setModalValidationErrors] = useState<IModalValidationErrors>({});

  const autocompleteOriginRef = useRef<IGooglePlaceAutocompleteElement | null>(null);
  const autocompleteDestinationRef = useRef<IGooglePlaceAutocompleteElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const placeIdOriginRef = useRef<string | null>(null);
  const placeIdDestinationRef = useRef<string | null>(null);

  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY || '',
    language: 'es',
    region: 'ES',
    version: 'weekly',
  });

  const modalValidationMessages = getValidationMessages(modalValidationErrors);

  const resetModalForm = () => {
    setTipo('');
    setTravelDate('');
    setImporte('');
    setOriginAddress('');
    setDestinationAddress('');
    setOriginHasSelectedPlace(false);
    setDestinationHasSelectedPlace(false);
    setSelectedFiles([]);
    setFileError(null);
    setSearchMessage(null);
    setModalValidationErrors({});
    placeIdOriginRef.current = null;
    placeIdDestinationRef.current = null;

    if (autocompleteOriginRef.current) {
      autocompleteOriginRef.current.value = '';
    }

    if (autocompleteDestinationRef.current) {
      autocompleteDestinationRef.current.value = '';
    }
  };

  const openAddModal = () => {
    resetModalForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const addSelectedFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const incomingFiles = Array.from(event.target.files ?? []);
    const validFiles = incomingFiles.filter((file) => {
      const extensionIndex = file.name.lastIndexOf('.');
      const extension = extensionIndex >= 0 ? file.name.slice(extensionIndex).toLowerCase() : '';
      return ALLOWED_FILE_EXTENSIONS.includes(extension);
    });
    const invalidFiles = incomingFiles.filter((file) => !validFiles.includes(file));

    setSelectedFiles((currentFiles) => [...currentFiles, ...validFiles]);
    if (validFiles.length > 0) {
      clearModalValidationError('tickets');
    }
    setFileError(
      invalidFiles.length > 0
        ? `No se han añadido ${invalidFiles.length === 1 ? 'el archivo no permitido' : 'los archivos no permitidos'}: ${invalidFiles.map((file) => file.name).join(', ')}.`
        : null
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeSelectedFile = (fileIndex: number) => {
    setSelectedFiles((currentFiles) => currentFiles.filter((_, index) => index !== fileIndex));
  };

  const removePreviewFile = (fileIndex: number) => {
    if (previewRowIndex === null) return;

    const expense = getValues(`${name}.${previewRowIndex}`) as IOtherVehicleExpenseValue | undefined;
    const files = Array.isArray(expense?.Archivos) ? expense.Archivos : [];
    const fileToRemove = files[fileIndex];

    if (!expense || !fileToRemove || files.length === 1) return;

    update(previewRowIndex, {
      ...expense,
      Ticket: null,
      Archivos: files.filter((_, index) => index !== fileIndex),
    });
    setTotalSize?.((currentSize) => Math.max(0, currentSize - getFileSizeMb([fileToRemove])));
  };

  const clearModalValidationError = (...keys: Array<keyof IModalValidationErrors>) => {
    setModalValidationErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      keys.forEach((key) => {
        delete nextErrors[key];
      });

      return nextErrors;
    });
  };

  const handleGoogleAddressChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    errorKey: keyof IModalValidationErrors
  ) => (value: string) => {
    setter(value);
    if (errorKey === 'origin') {
      setOriginHasSelectedPlace(Boolean(placeIdOriginRef.current));
    } else if (errorKey === 'destination') {
      setDestinationHasSelectedPlace(Boolean(placeIdDestinationRef.current));
    }
    setSearchMessage(null);
    clearModalValidationError(errorKey);
  };

  const getPersonalAddress = () => {
    const datosPersonales = getValues('DatosPersonales') || {};
    const addressParts = [
      datosPersonales?.SolicitanteDomicilio,
      datosPersonales?.SolicitanteCodigoPostal,
      datosPersonales?.SolicitanteComunidadCodigoDescripcion,
      datosPersonales?.SolicitanteProvinciaDescripcion,
    ];

    return addressParts
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(', ');
  };

  const applyPersonalAddressToField = async (field: 'origin' | 'destination') => {
    const personalAddress = getPersonalAddress();

    if (!personalAddress) {
      setSearchMessage('No hay domicilio personal informado en Datos Personales.');
      return;
    }

    const isOrigin = field === 'origin';
    const autocompleteRef = isOrigin ? autocompleteOriginRef : autocompleteDestinationRef;
    const placeIdRef = isOrigin ? placeIdOriginRef : placeIdDestinationRef;
    const setHasSelectedPlace = isOrigin ? setOriginHasSelectedPlace : setDestinationHasSelectedPlace;
    const setFieldValue = (value: string) => {
      (isOrigin ? setOriginAddress : setDestinationAddress)(value);
      if (autocompleteRef.current) {
        autocompleteRef.current.value = value;
      }
    };

    setFieldValue(personalAddress);
    placeIdRef.current = null;
    setHasSelectedPlace(false);
    setSearchMessage(null);
    clearModalValidationError(field);

    if (!isGoogleMapsLoaded || googleMapsLoadError || typeof google === 'undefined' || !google.maps?.Geocoder) {
      return;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({ address: personalAddress, region: 'ES' });
      const firstResult = response.results?.[0];

      if (firstResult) {
        const resolvedAddress = firstResult.formatted_address || personalAddress;
        setFieldValue(resolvedAddress);
        placeIdRef.current = firstResult.place_id || null;
        setHasSelectedPlace(Boolean(placeIdRef.current));
      }
    } catch (error: unknown) {
      placeIdRef.current = null;
      setHasSelectedPlace(false);
      setSearchMessage(
        getUnknownErrorMessage(
          error,
          'Se ha escrito el domicilio personal, pero Google Maps no ha podido validarlo.'
        )
      );
    }
  };

  const validateModalForm = () => {
    const nextErrors: IModalValidationErrors = {};

    if (!tipo) {
      nextErrors.tipo = 'Selecciona el tipo de transporte.';
    }

    if (!travelDate) {
      nextErrors.travelDate = 'Selecciona la fecha del viaje.';
    } else if (travelDate > getTodayInputValue()) {
      nextErrors.travelDate = 'La fecha del viaje no puede ser futura.';
    }

    if (!importe.trim()) {
      nextErrors.importe = 'Introduce el importe del viaje.';
    } else if (!/^\d+(?:[.,]\d{1,2})?$/.test(importe.trim()) || (parseImporte(importe) ?? 0) <= 0) {
      nextErrors.importe = 'Introduce un importe mayor que cero con un máximo de dos decimales.';
    }

    if (!originAddress.trim()) {
      nextErrors.origin = 'Selecciona el origen del viaje.';
    } else if (!placeIdOriginRef.current) {
      nextErrors.origin = 'Selecciona el origen desde el listado de Google.';
    }

    if (!destinationAddress.trim()) {
      nextErrors.destination = 'Selecciona el destino del viaje.';
    } else if (!placeIdDestinationRef.current) {
      nextErrors.destination = 'Selecciona el destino desde el listado de Google.';
    }

    if (selectedFiles.length === 0) {
      nextErrors.tickets = 'Adjunta al menos un documento justificativo del gasto.';
    }

    setModalValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const addExpense = () => {
    if (!validateModalForm()) {
      return;
    }

    const selectedType = OTHER_VEHICLE_TYPE_OPTIONS.find((option) => option.value === tipo) ?? null;
    const expense: IOtherVehicleExpenseValue = {
      Tipo: tipo,
      TipoDescripcion: selectedType?.label ?? null,
      FechaViaje: travelDate,
      Importe: parseImporte(importe),
      DireccionOrigen: originAddress.trim(),
      DireccionDestino: destinationAddress.trim(),
      OrigenGooglePlaceId: placeIdOriginRef.current,
      DestinoGooglePlaceId: placeIdDestinationRef.current,
      Ticket: null,
      Archivos: selectedFiles,
      SasUri: null,
      FechaPresentacion: null,
      Nombre: null,
      NombreStorage: null,
      Extension: null,
    };

    append(expense);
    setTotalSize?.((currentSize) => currentSize + getFileSizeMb(selectedFiles));
    void trigger([name, ...additionalRequiredNames]);
    closeModal();
  };

  const removeExpense = (rowIndex: number) => {
    const expense = getValues(`${name}.${rowIndex}`) as IOtherVehicleExpenseValue | undefined;
    const files = Array.isArray(expense?.Archivos) ? expense.Archivos : [];

    setTotalSize?.((currentSize) => Math.max(0, currentSize - getFileSizeMb(files)));
    remove(rowIndex);
    void trigger([name, ...additionalRequiredNames]);
  };

  const previewExpense = previewRowIndex === null
    ? null
    : expenseRows[previewRowIndex];
  const previewFiles = Array.isArray(previewExpense?.Archivos) ? previewExpense.Archivos : [];
  const downloadExpense = downloadRowIndex === null
    ? null
    : expenseRows[downloadRowIndex];

  const downloadPersistedFiles = async () => {
    if (!downloadExpense?.SasUri) return;

    setIsDownloading(true);

    try {
      const response = await safeFetch(downloadExpense.SasUri);
      if (!response.ok) throw new Error('Error al descargar');

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getPersistedDownloadName(downloadExpense);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      setDownloadRowIndex(null);
    } catch {
      toast.error('No se pudieron descargar los archivos presentados.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col} ${invisible ? 'd-none' : ''}`}>
      <div data-input-name={name} className={`calculadora-gastos-otros-vehiculos ${errorMessage ? 'error' : ''}`}>
        <div className="calculadora-gastos-otros-vehiculos__table-block">
          <div className="calculadora-gastos-otros-vehiculos__table-header">
            <div className="calculadora-gastos-otros-vehiculos__table-heading">
              <div className="calculadora-gastos-otros-vehiculos__label-row">
                <label className="calculadora-gastos-otros-vehiculos__label">{label}</label>
                <button
                  type="button"
                  className="calculadora-gastos-otros-vehiculos__info-button"
                  onClick={() => setShowInfoModal(true)}
                  aria-label="Información sobre otros vehículos"
                  title="Información sobre otros vehículos"
                >
                  <span className="information-icon-orange p-0" aria-hidden="true" />
                </button>
              </div>
              <span className="calculadora-gastos-otros-vehiculos__table-count">
                {expenseRows.length === 1 ? '1 gasto añadido' : `${expenseRows.length} gastos añadidos`}
              </span>
            </div>

            <button
              type="button"
              className="calculadora-gastos-otros-vehiculos__add-button"
              onClick={openAddModal}
              disabled={disabled}
            >
              <span aria-hidden="true">+</span>
              <span>Agregar</span>
            </button>
          </div>

          <div className="calculadora-gastos-otros-vehiculos__table-shell">
            <div className="calculadora-gastos-otros-vehiculos__table-scroll">
              <table className="calculadora-gastos-otros-vehiculos__table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Fecha viaje</th>
                    <th>Importe</th>
                    <th>Origen</th>
                    <th>Destino</th>
                    <th>Tickets</th>
                    <th className="calculadora-gastos-otros-vehiculos__actions-head" aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {expenseRows.map((expense, rowIndex) => (
                    <tr key={expense.id}>
                      <td data-label="Tipo">{expense.TipoDescripcion ?? getTypeLabel(expense.Tipo)}</td>
                      <td data-label="Fecha viaje">{formatDateForDisplay(expense.FechaViaje)}</td>
                      <td data-label="Importe" className="calculadora-gastos-otros-vehiculos__amount-cell">
                        {formatImporte(expense.Importe)}
                      </td>
                      <td data-label="Origen" className="calculadora-gastos-otros-vehiculos__route-cell">
                        {expense.DireccionOrigen || '-'}
                      </td>
                      <td data-label="Destino" className="calculadora-gastos-otros-vehiculos__route-cell">
                        {expense.DireccionDestino || '-'}
                      </td>
                      <td data-label="Ticket">
                        {expense.SasUri ? (
                          <button
                            type="button"
                            className="calculadora-gastos-otros-vehiculos__ticket-button calculadora-gastos-otros-vehiculos__ticket-button--download"
                            onClick={() => setDownloadRowIndex(rowIndex)}
                            aria-label={`Descargar archivos presentados del gasto ${rowIndex + 1}`}
                            title="Descargar archivos presentados"
                          >
                            <Icon name="download" size={19} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="calculadora-gastos-otros-vehiculos__ticket-button"
                            onClick={() => setPreviewRowIndex(rowIndex)}
                            aria-label={`Ver archivos del gasto ${rowIndex + 1}`}
                            title="Ver archivos seleccionados"
                          >
                            <Icon name="eye" size={19} />
                          </button>
                        )}
                      </td>
                      <td className="calculadora-gastos-otros-vehiculos__actions-cell" data-label="Acciones">
                        <button
                          type="button"
                          className="calculadora-gastos-otros-vehiculos__remove-button"
                          onClick={() => removeExpense(rowIndex)}
                          aria-label={`Eliminar gasto ${rowIndex + 1}`}
                          disabled={disabled}
                        >
                          <img src={Delete} alt="" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {expenseRows.length === 0 && (
                    <tr className="calculadora-gastos-otros-vehiculos__empty-row">
                      <td colSpan={7}>Sin gastos añadidos.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {errorMessage && <p className="calculadora-gastos-otros-vehiculos__error m-0">{errorMessage}</p>}
        </div>

        <Modal
          show={showInfoModal}
          onHide={() => setShowInfoModal(false)}
          className="calculadora-gastos-otros-vehiculos calculadora-gastos-otros-vehiculos--info"
          centered
        >
          <Modal.Header closeButton className="calculadora-gastos-otros-vehiculos__modal-header">
            <Modal.Title>Otros vehículos</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Se considera Otros Vehículos: Taxi, Transporte Público, UBER/VTC o cualquier otro transporte que genere un documento justificativo del gasto.</p>
            <p>Se considera documento justificativo del gasto (ticket, factura o comprobante de transporte).</p>
          </Modal.Body>
        </Modal>

        <Modal show={showModal} onHide={closeModal} className="calculadora-gastos-otros-vehiculos" centered>
          <Modal.Header closeButton className="calculadora-gastos-otros-vehiculos__modal-header">
            <Modal.Title>Nuevo gasto en otros vehículos</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="calculadora-gastos-otros-vehiculos__modal-body">
              {modalValidationMessages.length > 0 && (
                <div className="calculadora-gastos-otros-vehiculos__validation-summary" role="alert" aria-live="polite">
                  <strong>Revisa el gasto antes de añadirlo</strong>
                  <ul>
                    {modalValidationMessages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="calculadora-gastos-otros-vehiculos__modal-grid">
                <div className={`calculadora-gastos-otros-vehiculos__modal-field ${modalValidationErrors.tipo ? 'calculadora-gastos-otros-vehiculos__modal-field--error' : ''}`}>
                  <label htmlFor={`${modalBaseId}-tipo`}>Tipo</label>
                  <select
                    id={`${modalBaseId}-tipo`}
                    value={tipo}
                    onChange={(event) => {
                      setTipo(event.target.value);
                      clearModalValidationError('tipo');
                    }}
                    className="calculadora-gastos-otros-vehiculos__modal-input"
                  >
                    <option value="">Selecciona un tipo</option>
                    {OTHER_VEHICLE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className={`calculadora-gastos-otros-vehiculos__modal-field ${modalValidationErrors.travelDate ? 'calculadora-gastos-otros-vehiculos__modal-field--error' : ''}`}>
                  <label htmlFor={`${modalBaseId}-fecha`}>Fecha viaje</label>
                  <input
                    id={`${modalBaseId}-fecha`}
                    type="date"
                    max={getTodayInputValue()}
                    value={travelDate}
                    onChange={(event) => {
                      setTravelDate(event.target.value);
                      clearModalValidationError('travelDate');
                    }}
                    className="calculadora-gastos-otros-vehiculos__modal-input"
                  />
                </div>
              </div>

              <div className={`calculadora-gastos-otros-vehiculos__modal-field ${modalValidationErrors.importe ? 'calculadora-gastos-otros-vehiculos__modal-field--error' : ''}`}>
                <label htmlFor={`${modalBaseId}-importe`}>Importe</label>
                <input
                  id={`${modalBaseId}-importe`}
                  type="text"
                  inputMode="decimal"
                  value={importe}
                  aria-invalid={Boolean(modalValidationErrors.importe)}
                  onChange={(event) => {
                    setImporte(event.target.value);
                    clearModalValidationError('importe');
                  }}
                  placeholder="0,00"
                  className="calculadora-gastos-otros-vehiculos__modal-input"
                />
              </div>

              <div className="calculadora-gastos-otros-vehiculos__route-panel">
                <div className={`calculadora-gastos-otros-vehiculos__modal-field ${modalValidationErrors.origin ? 'calculadora-gastos-otros-vehiculos__modal-field--error' : ''}`}>
                  <GooglePlaceAutocomplete
                    id={`${modalBaseId}-origen`}
                    initialValue={originAddress}
                    isGoogleMapsLoaded={isGoogleMapsLoaded}
                    hasSelectedPlace={originHasSelectedPlace}
                    label="Origen"
                    placeholder="Ejemplo: Toledo"
                    onUsePersonalAddress={() => void applyPersonalAddressToField('origin')}
                    personalAddressDisabled={disabled}
                    placeIdRef={placeIdOriginRef}
                    valueRef={autocompleteOriginRef}
                    onChange={handleGoogleAddressChange(setOriginAddress, 'origin')}
                    onError={setSearchMessage}
                  />
                </div>

                <div className={`calculadora-gastos-otros-vehiculos__modal-field ${modalValidationErrors.destination ? 'calculadora-gastos-otros-vehiculos__modal-field--error' : ''}`}>
                  <GooglePlaceAutocomplete
                    id={`${modalBaseId}-destino`}
                    initialValue={destinationAddress}
                    isGoogleMapsLoaded={isGoogleMapsLoaded}
                    hasSelectedPlace={destinationHasSelectedPlace}
                    label="Destino"
                    placeholder="Ejemplo: Madrid"
                    onUsePersonalAddress={() => void applyPersonalAddressToField('destination')}
                    personalAddressDisabled={disabled}
                    placeIdRef={placeIdDestinationRef}
                    valueRef={autocompleteDestinationRef}
                    onChange={handleGoogleAddressChange(setDestinationAddress, 'destination')}
                    onError={setSearchMessage}
                  />
                </div>
              </div>

              <div className={`calculadora-gastos-otros-vehiculos__files-panel ${modalValidationErrors.tickets ? 'calculadora-gastos-otros-vehiculos__files-panel--error' : ''}`}>
                <div className="calculadora-gastos-otros-vehiculos__files-heading">
                  <div>
                    <strong>Tickets (obligatorio)</strong>
                    <span>
                      {selectedFiles.length === 1 ? '1 archivo seleccionado' : `${selectedFiles.length} archivos seleccionados`}
                      {selectedFiles.length > 0 && ` · ${getFileSizeMb(selectedFiles).toFixed(2)} MB`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="calculadora-gastos-otros-vehiculos__select-files-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}
                    aria-describedby={modalValidationErrors.tickets ? `${modalBaseId}-tickets-error` : undefined}
                  >
                    <Icon name="paperclip" size={17} />
                    <span>Seleccionar archivos</span>
                  </button>
                  <input
                    ref={fileInputRef}
                    id={`${modalBaseId}-archivos`}
                    type="file"
                    multiple
                    accept={ALLOWED_FILE_EXTENSIONS.join(',')}
                    onChange={addSelectedFiles}
                    className="calculadora-gastos-otros-vehiculos__file-input"
                  />
                </div>
                <p className="calculadora-gastos-otros-vehiculos__files-requirement">
                  Adjunta al menos un documento justificativo antes de añadir el gasto.
                </p>
                {selectedFiles.length > 0 && (
                  <ul className="calculadora-gastos-otros-vehiculos__files-list calculadora-gastos-otros-vehiculos__files-list--selected" aria-label="Tickets seleccionados">
                    {selectedFiles.map((file, fileIndex) => (
                      <li key={`${file.name}-${file.size}-${file.lastModified}-${fileIndex}`}>
                        <Icon name="file-text" size={20} />
                        <div>
                          <strong>{file.name}</strong>
                          <span>{formatFileSize(file.size)}</span>
                        </div>
                        <button
                          type="button"
                          className="calculadora-gastos-otros-vehiculos__remove-file-button"
                          onClick={() => removeSelectedFile(fileIndex)}
                          aria-label={`Eliminar archivo ${file.name}`}
                          disabled={disabled}
                        >
                          <img src={Delete} alt="" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {modalValidationErrors.tickets && (
                  <p id={`${modalBaseId}-tickets-error`} className="calculadora-gastos-otros-vehiculos__error m-0" role="alert">
                    {modalValidationErrors.tickets}
                  </p>
                )}
                {fileError && <p className="calculadora-gastos-otros-vehiculos__error m-0" role="alert">{fileError}</p>}
              </div>

              {!isGoogleMapsLoaded && !googleMapsLoadError && (
                <div className="calculadora-gastos-otros-vehiculos__status">Cargando Google Maps...</div>
              )}

              {googleMapsLoadError && (
                <p className="calculadora-gastos-otros-vehiculos__error m-0">
                  No se pudo cargar Google Maps. Revisa la API key.
                </p>
              )}

              {searchMessage && (
                <p className="calculadora-gastos-otros-vehiculos__error m-0">{searchMessage}</p>
              )}

              <div className="calculadora-gastos-otros-vehiculos__action-row">
                <button
                  type="button"
                  className="calculadora-gastos-otros-vehiculos__accept-button"
                  onClick={addExpense}
                  disabled={!isGoogleMapsLoaded}
                >
                  Añadir
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={previewRowIndex !== null}
          onHide={() => setPreviewRowIndex(null)}
          className="calculadora-gastos-otros-vehiculos"
          centered
        >
          <Modal.Header closeButton className="calculadora-gastos-otros-vehiculos__modal-header">
            <Modal.Title>Archivos seleccionados</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="calculadora-gastos-otros-vehiculos__files-modal-body">
              <p>
                Consulta el nombre y tamaño de los tickets asociados a este gasto. Puedes eliminar archivos, pero debe quedar al menos uno por gasto.
              </p>
              {previewFiles.length > 0 ? (
                <ul className="calculadora-gastos-otros-vehiculos__files-list">
                  {previewFiles.map((file, fileIndex) => (
                    <li key={`${file.name}-${file.size}-${file.lastModified}-${fileIndex}`}>
                      <Icon name="file-text" size={20} />
                      <div>
                        <strong>{file.name}</strong>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="calculadora-gastos-otros-vehiculos__remove-file-button"
                        onClick={() => removePreviewFile(fileIndex)}
                        aria-label={`Eliminar archivo ${file.name}`}
                        disabled={disabled || previewFiles.length === 1}
                      >
                        <img src={Delete} alt="" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="calculadora-gastos-otros-vehiculos__empty-files">No hay archivos seleccionados para este gasto.</p>
              )}
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={downloadRowIndex !== null}
          onHide={() => setDownloadRowIndex(null)}
          className="calculadora-gastos-otros-vehiculos"
          centered
        >
          <Modal.Header closeButton className="calculadora-gastos-otros-vehiculos__modal-header">
            <Modal.Title>Descargar archivos presentados</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="calculadora-gastos-otros-vehiculos__download-modal-body">
              <p>
                ¿Desea descargar los archivos presentados el: {' '}
                <strong>{formatPresentationDate(downloadExpense?.FechaPresentacion)}</strong>?
              </p>
              <div className="calculadora-gastos-otros-vehiculos__download-actions">
                <button
                  type="button"
                  className="calculadora-gastos-otros-vehiculos__cancel-button"
                  onClick={() => setDownloadRowIndex(null)}
                  disabled={isDownloading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="calculadora-gastos-otros-vehiculos__confirm-download-button"
                  onClick={() => void downloadPersistedFiles()}
                  disabled={isDownloading}
                >
                  <Icon name="download" size={17} />
                  <span>{isDownloading ? 'Descargando...' : 'Confirmar'}</span>
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default CalculadoraGastosOtrosVehiculos;
